import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/supabase";
import { isLocalDemoEnabled } from "@/lib/localDemo";

export type AppRole = "admin" | "project_manager" | "site_manager" | "employee" | "owner";

const VIEW_ROLE_KEY = "timetrack-current-role";
const APP_ROLES: AppRole[] = ["admin", "project_manager", "site_manager", "employee", "owner"];
let cachedRoleState: RoleState | null = null;
let pendingRoleState: Promise<RoleState> | null = null;
let cachedAuthUserId: string | null = null;

const normalizeRole = (stored: string | null): AppRole => {
  if (stored === "projektleder") return "project_manager";
  if (stored === "byggeleder") return "site_manager";
  if (stored === "medarbejder" || stored === "viewer") return "employee";
  if (stored === "ejer") return "owner";
  if (APP_ROLES.includes(stored as AppRole)) return stored as AppRole;
  return "employee";
};

const roleRank: Record<AppRole, number> = {
  employee: 1,
  site_manager: 2,
  project_manager: 3,
  admin: 4,
  owner: 5,
};

const rolesForAccount = (accountRole: AppRole | null): AppRole[] => {
  if (accountRole === "owner") return ["admin", "project_manager", "site_manager", "employee", "owner"];
  if (accountRole === "admin") return ["admin", "project_manager", "site_manager", "employee"];
  return accountRole ? [accountRole] : [];
};

const resolveViewRole = (accountRole: AppRole | null, defaultRole = accountRole): AppRole => {
  if (!accountRole) return "employee";
  const storedViewRole = normalizeRole(localStorage.getItem(VIEW_ROLE_KEY));
  const normalizedDefaultRole = normalizeRole(defaultRole);
  if (rolesForAccount(accountRole).includes(storedViewRole)) return storedViewRole;
  return rolesForAccount(accountRole).includes(normalizedDefaultRole) ? normalizedDefaultRole : accountRole;
};

interface RoleState {
  loading: boolean;
  isAuthenticated: boolean;
  accountRole: AppRole | null;
  role: AppRole;
  profileId: string | null;
  profileName: string | null;
  companyId: string | null;
}

const initialRoleState: RoleState = {
  loading: true,
  isAuthenticated: false,
  accountRole: null,
  role: "employee",
  profileId: null,
  profileName: null,
  companyId: null,
};

const unauthenticatedRoleState: RoleState = {
  loading: false,
  isAuthenticated: false,
  accountRole: null,
  role: "employee",
  profileId: null,
  profileName: null,
  companyId: null,
};

export const getCachedCompanyId = () => cachedRoleState?.companyId ?? null;
export const getCachedProfileContext = () => {
  if (!cachedRoleState?.companyId || !cachedRoleState.profileId) return null;
  return { companyId: cachedRoleState.companyId, profileId: cachedRoleState.profileId, profileName: cachedRoleState.profileName };
};
export const clearCurrentRoleCache = () => {
  cachedRoleState = null;
  pendingRoleState = null;
  cachedAuthUserId = null;
};

const resolveRoleState = async (): Promise<RoleState> => {
  if (cachedRoleState && !cachedRoleState.loading && cachedRoleState.isAuthenticated) return cachedRoleState;
  if (pendingRoleState) return pendingRoleState;

  pendingRoleState = (async () => {
    if (isLocalDemoEnabled()) {
      return { loading: false, isAuthenticated: true, accountRole: "employee", role: "employee", profileId: null, profileName: null, companyId: null };
    }

    const clearAccess = async () => {
      localStorage.removeItem(VIEW_ROLE_KEY);
      await supabase.auth.signOut();
      return unauthenticatedRoleState;
    };

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) return clearAccess();
    if (!sessionData.session) {
      localStorage.removeItem(VIEW_ROLE_KEY);
      return unauthenticatedRoleState;
    }

    const authUserId = sessionData.session.user.id;
    if (!authUserId) return clearAccess();
    cachedAuthUserId = authUserId;

    const { data, error } = await supabase
      .from("profiles")
      .select("id,name,role,is_platform_owner,company_id")
      .eq("auth_user_id", authUserId)
      .eq("status", "active")
      .maybeSingle();

    if (error || !data?.role) return clearAccess();

    const profileRole = normalizeRole(data.role);
    const accountRole = data.is_platform_owner ? "owner" : profileRole;
    return {
      loading: false,
      isAuthenticated: true,
      accountRole,
      role: resolveViewRole(accountRole, profileRole),
      profileId: data.id ?? null,
      profileName: data.name ?? null,
      companyId: data.company_id ?? null,
    };
  })();

  try {
    cachedRoleState = await pendingRoleState;
    return cachedRoleState;
  } finally {
    pendingRoleState = null;
  }
};

const getInitialRoleState = (): RoleState => {
  if (cachedRoleState) {
    return cachedRoleState;
  }

  const storedViewRole = normalizeRole(typeof window !== "undefined" ? window.localStorage.getItem(VIEW_ROLE_KEY) : null);
  return {
    ...initialRoleState,
    role: storedViewRole,
  };
};

export function useCurrentRole() {
  const [state, setState] = useState<RoleState>(getInitialRoleState);

  const loadRole = useCallback(async () => {
    const nextState = await resolveRoleState();
    setState(nextState);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const guardedLoadRole = async () => {
      if (cancelled) return;
      await loadRole();
    };

    if (!cachedRoleState || cachedRoleState.loading) {
      void guardedLoadRole();
    }

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "INITIAL_SESSION") return;
      const eventUserId = session?.user?.id ?? null;
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && pendingRoleState) return;
      if (event === "SIGNED_IN" && eventUserId === cachedAuthUserId && cachedRoleState?.isAuthenticated) return;
      if (event === "TOKEN_REFRESHED" && eventUserId === cachedAuthUserId) return;
      clearCurrentRoleCache();
      void guardedLoadRole();
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, [loadRole]);

  const setRole = useCallback((newRole: AppRole) => {
    setState((current) => {
      if (!rolesForAccount(current.accountRole).includes(newRole)) return current;
      localStorage.setItem(VIEW_ROLE_KEY, newRole);
      const nextState = { ...current, role: newRole };
      cachedRoleState = nextState;
      return nextState;
    });
  }, []);

  const { loading, isAuthenticated, accountRole, role } = state;
  const canEditProjects = role === "admin" || role === "project_manager";
  const canSeeAllProjects = role === "admin" || role === "project_manager";
  const isByggeleder = role === "site_manager";
  const isMedarbejder = role === "employee";
  const isOwner = role === "owner";
  const isOwnerAccount = accountRole === "owner";
  const isAdminAccount = accountRole === "admin" || accountRole === "owner";

  return {
    loading,
    isAuthenticated,
    role,
    accountRole,
    setRole,
    canEditProjects,
    canSeeAllProjects,
    isByggeleder,
    isMedarbejder,
    isOwner,
    isOwnerAccount,
    isAdminAccount,
    roleRank,
  };
}
